import React, { useState } from 'react';
import { EFFECT_DEFINITIONS, EffectCategory, EffectDefinition, EffectMarker } from './types';

interface EffectsPanelProps {
  currentTime: number;
  markers: number[];
  selectedMarkerIndex: number | null;
  onAddEffect: (marker: Omit<EffectMarker, 'id'>) => void;
}

const CATEGORY_NAMES: Record<EffectCategory, string> = {
  clip: 'Эффекты клипов',
  transition: 'Переходы',
  timed: 'Временные эффекты',
};

const CATEGORIES: EffectCategory[] = ['clip', 'transition', 'timed'];

function findNearestMarker(time: number, markers: number[]): number | null {
  if (markers.length === 0) return null;
  let nearest = markers[0];
  let minDist = Math.abs(time - nearest);
  for (const m of markers) {
    const dist = Math.abs(time - m);
    if (dist < minDist) {
      minDist = dist;
      nearest = m;
    }
  }
  return nearest;
}

interface EffectRowProps {
  def: EffectDefinition;
  currentTime: number;
  markers: number[];
  selectedMarkerIndex: number | null;
  onAdd: (marker: Omit<EffectMarker, 'id'>) => void;
}

function EffectRow({ def, currentTime, markers, selectedMarkerIndex, onAdd }: EffectRowProps) {
  const [selectedDirection, setSelectedDirection] = useState(def.directions?.[0] ?? '');
  const [selectedVariant, setSelectedVariant] = useState(def.variants?.[0] ?? '1');
  const [selectedColor, setSelectedColor] = useState(def.colorOptions?.[0] ?? 'DEF');
  const [duration, setDuration] = useState(def.defaultDuration?.toString() ?? '');

  const disabled = def.category === 'transition' && markers.length === 0;

  const handleAdd = () => {
    if (disabled) return;
    let code = def.code;
    const params: Record<string, string | number> = {};

    if (def.directions) code = selectedDirection;
    if (def.variants) {
      code = `${def.code}${selectedVariant}`;
      params.variant = selectedVariant;
    }
    if (def.hasColor) {
      code = `C_${selectedColor}`;
      params.color = selectedColor;
    }
    if (def.hasDuration) {
      params.duration = parseFloat(duration) || def.defaultDuration || 0;
    }

    // For transitions, snap to nearest marker
    let time = Math.round(currentTime * 100) / 100;
    if (def.category === 'transition') {
      if (selectedMarkerIndex !== null && selectedMarkerIndex < markers.length) {
        time = markers[selectedMarkerIndex];
      } else {
        const nearest = findNearestMarker(currentTime, markers);
        if (nearest !== null) time = nearest;
      }
    }

    onAdd({ effectId: def.id, effectCode: code, category: def.category, time, params });
  };

  const hasControls = def.directions || def.variants || def.hasColor || def.hasDuration;

  return (
    <div className="at-effect-row">
      <div className="at-effect-dot" style={{ backgroundColor: def.color }} />
      <span className="at-effect-name-compact">{def.name}</span>
      <div className="at-effect-controls">
        {def.directions && (
          <select
            value={selectedDirection}
            onChange={(e) => setSelectedDirection(e.target.value)}
            className="at-effect-select-compact"
          >
            {def.directions.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        )}
        {def.variants && (
          <select
            value={selectedVariant}
            onChange={(e) => setSelectedVariant(e.target.value)}
            className="at-effect-select-compact"
          >
            {def.variants.map((v) => (
              <option key={v} value={v}>{def.code}{v}</option>
            ))}
          </select>
        )}
        {def.hasColor && (
          <select
            value={selectedColor}
            onChange={(e) => setSelectedColor(e.target.value)}
            className="at-effect-select-compact"
          >
            {def.colorOptions!.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        )}
        {def.hasDuration && (
          <input
            type="number"
            step="0.01"
            min="0.01"
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            className="at-effect-input-compact"
            placeholder="сек"
          />
        )}
        {!hasControls && <div style={{ flex: 1 }} />}
      </div>
      <button
        onClick={handleAdd}
        disabled={disabled}
        className="at-effect-add-btn"
        title={disabled ? 'Сначала добавьте склейки' : 'Добавить эффект'}
      >
        +
      </button>
    </div>
  );
}

export function EffectsPanel({ currentTime, markers, selectedMarkerIndex, onAddEffect }: EffectsPanelProps) {
  const grouped = CATEGORIES.map((cat) => ({
    category: cat,
    label: CATEGORY_NAMES[cat],
    definitions: EFFECT_DEFINITIONS.filter((d) => d.category === cat),
  }));

  return (
    <div className="at-effects-panel-compact">
      <div className="at-effects-panel-header">Эффекты</div>
      <div className="at-effects-scroll-area">
        {grouped.map(({ category, label, definitions }) => (
          <div key={category} className="at-effect-section">
            <div className="at-effect-section-header">{label}</div>
            {definitions.map((def) => (
              <EffectRow
                key={def.id}
                def={def}
                currentTime={currentTime}
                markers={markers}
                selectedMarkerIndex={selectedMarkerIndex}
                onAdd={onAddEffect}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
